import React, { useState } from "react";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";

import {
  ISD_FLAG_BY_CODE,
  isIsdVisibleInDropdown,
} from "constants/omsRegionConfig";
import { getPhoneConfigForIsd } from "constants/phoneConfig";
import { getPhoneValidationError } from "utils/phoneValidation";
import { useCountryIsdList } from "services/mdsService";
import Textfield from "widgets/Textfield/Textfield";

import styles from "./PhoneWithIsdField.module.scss";

export const COUNTRY_LIST_ERROR_MESSAGE =
  "Failed to load country list. Please refresh the page.";

const PhoneWithIsdField = ({
  id,
  label,
  isdCode,
  defaultIsdCode,
  phone,
  onIsdCodeChange,
  onPhoneChange,
  onPhoneBlur,
  getValidationError,
  error: externalError,
  helperText,
  disabled = false,
}) => {
  const [phoneTouched, setPhoneTouched] = useState(false);
  const effectiveIsdCode = defaultIsdCode || isdCode;
  const showIsdSelector = !defaultIsdCode;
  const resolveValidationError =
    getValidationError ||
    ((value) => getPhoneValidationError(value, effectiveIsdCode));

  const {
    data: isdMenuItems = [],
    isLoading: isLoadingCountries,
    isError: isCountryListError,
  } = useCountryIsdList();

  const visibleIsdMenuItems = isdMenuItems.filter((item) =>
    isIsdVisibleInDropdown(item.value),
  );

  const maxPhoneLength = getPhoneConfigForIsd(effectiveIsdCode).maxLength;
  const selectedIsdValue = effectiveIsdCode || "";
  const hasSelectedInList = visibleIsdMenuItems.some(
    (item) => item.value === selectedIsdValue,
  );

  const formatOrphanIsdLabel = (code) => {
    const flag = ISD_FLAG_BY_CODE[code];
    return flag ? `${flag} ${code}` : code;
  };

  const internalError = phoneTouched ? resolveValidationError(phone) : null;
  const displayError = showIsdSelector && isCountryListError
    ? true
    : externalError !== undefined
      ? externalError
      : Boolean(internalError);
  const displayHelperText = showIsdSelector && isCountryListError
    ? COUNTRY_LIST_ERROR_MESSAGE
    : helperText !== undefined
      ? helperText
      : internalError || "";

  const markPhoneTouched = (event) => {
    setPhoneTouched(true);
    onPhoneBlur?.(event);
  };

  const handleIsdChange = (event) => {
    const nextIsdCode = event.target.value;
    const nextMaxLength = getPhoneConfigForIsd(nextIsdCode).maxLength;
    onIsdCodeChange(nextIsdCode);
    onPhoneChange(phone.slice(0, nextMaxLength));
    setPhoneTouched(true);
  };

  const handlePhoneChange = (event) => {
    onPhoneChange(
      event.target.value.replace(/\D/g, "").slice(0, maxPhoneLength),
    );
  };

  const isIsdDisabled = disabled || isLoadingCountries || isCountryListError;

  return (
    <div className={styles.phoneWithIsdField}>
      {showIsdSelector && (
        <div className={styles.isdSelector}>
          <FormControl fullWidth disabled={isIsdDisabled}>
            <InputLabel id={`${id}-isd-label`}>ISD</InputLabel>
            <Select
              labelId={`${id}-isd-label`}
              id={`${id}-isd`}
              value={selectedIsdValue}
              label="ISD"
              onChange={handleIsdChange}
            >
              {selectedIsdValue && !hasSelectedInList && (
                <MenuItem value={selectedIsdValue}>
                  {formatOrphanIsdLabel(selectedIsdValue)}
                </MenuItem>
              )}
              {visibleIsdMenuItems.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}
      <div className={styles.phoneInput}>
        <Textfield
          id={id}
          name={id}
          label={label}
          type="tel"
          autoComplete="tel"
          value={phone}
          handleOnChange={handlePhoneChange}
          onBlur={markPhoneTouched}
          disabled={disabled}
          error={displayError}
          helperText={displayHelperText}
        />
      </div>
    </div>
  );
};

export default PhoneWithIsdField;
